import { mountOnlyOfficeEditor, clearOnlyOfficeHost } from './onlyOfficeMount';
import { canEditSessionDocument, signatureAction } from './signatureAccess';
import { createSignatureFlow } from './signatureFlow';
import { displayText } from '../../terminology/store';
import { useTerminology } from '../../terminology/TerminologyContext';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignatureCanvas from "react-signature-canvas";
import {
  getDocumentEditorConfig,
  getSessionStatus,
  uploadDocumentSignature,
} from '../services/api';
import ThemeContext from '../ThemeContext';

// Fallback typography
const fallbackTypography = {
  fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  titleFontSize: 24,
  bodyFontSize: 14,
};

const Presentation = () => {
  useTerminology();
  const { state } = useLocation();
  const navigate = useNavigate();

  const editorInstance = useRef(null);
  const editorHost = useRef(null);
  const scriptLoaded = useRef(false);
  const pollRef = useRef(null);
  const bcRef = useRef(null);
  const autoRedirectTimeoutRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [docName, setDocName] = useState('Waiting for document...');
  const [sessionId, setSessionId] = useState(null);
  const [role, setRole] = useState(null);
  const [actionLabel, setActionLabel] = useState('Close');
  const [clientId, setClientId] = useState(null);

  const [currentEditor, setCurrentEditor] = useState(null);
  const [currentCanEdit, setCurrentCanEdit] = useState(false);
  const [currentDocId, setCurrentDocId] = useState(null);
  const [sessionFinished, setSessionFinished] = useState(false);

  // Signature States
  const [isSignable, setIsSignable] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [signaturePlacementPending, setSignaturePlacementPending] = useState(false);
  const signatureFlow = useRef(null);
  const editorLoad = useRef(0);
  const requestedEdit = useRef(null);
  if (!signatureFlow.current) signatureFlow.current = createSignatureFlow(async (id, image, command) =>
    (await uploadDocumentSignature(id, image, command)).data);

  const signaturePadRef = useRef(null);

  const { theme } = useContext(ThemeContext) || {};
  const typography = theme?.typography || fallbackTypography;
  const fontFamily = typography.fontFamily || fallbackTypography.fontFamily;
  const titleFontSize = typography.titleFontSize || fallbackTypography.titleFontSize;
  const bodyFontSize = typography.bodyFontSize || fallbackTypography.bodyFontSize;

  const resolveIdentity = () => {
    const joinReq = JSON.parse(localStorage.getItem('join_request') || 'null');
    const currentSession = JSON.parse(localStorage.getItem('currentSession') || 'null');
    const localUser = JSON.parse(localStorage.getItem('user') || 'null');

    const rawRole = joinReq?.type || currentSession?.userRole || localUser?.role || 'guest';
    const resolvedRole = (rawRole || 'guest').toString().trim().toLowerCase();

    let displayName = 'Guest';
    if (joinReq && (joinReq.first_name || joinReq.last_name)) {
      displayName = `${joinReq.first_name || ''} ${joinReq.last_name || ''}`.trim() || joinReq.email || 'Guest';
    } else if (localUser) {
      displayName = localUser.full_name || localUser.username || localUser.email || 'You';
    }

    const cid = joinReq?.id
      ? String(joinReq.id)
      : localUser?.id
      ? String(localUser.id)
      : `guest-${Date.now()}`;

    return { resolvedRole, displayName, clientId: cid };
  };

  const destroyEditor = () => {
    editorLoad.current += 1;
    signatureFlow.current.clear();
    setSignaturePlacementPending(false);
    setShowSignatureModal(false);
    if (editorInstance.current) {
      try {
        editorInstance.current.destroyEditor();
      } catch (e) {}
      editorInstance.current = null;
    }
    clearOnlyOfficeHost(editorHost.current);
  };

  const clearDocument = () => {
  destroyEditor();
  setDocName('Waiting for document from presenter...');
  setCurrentDocId(null);
  setIsSignable(false);
  setIsLoading(false);
  setShowSignatureModal(false);
};

  const getCurrentEditorForSession = async (sid) => {
    try {
      const stored = localStorage.getItem(`presentation_editor_${sid}`);
      if (stored) return JSON.parse(stored);
    } catch (e) {}

    try {
      const res = await getSessionStatus(sid);
      const data = res?.data?.data || res?.data || {};
      if (data.current_editor) {
        localStorage.setItem(`presentation_editor_${sid}`, JSON.stringify(data.current_editor));
        return data.current_editor;
      }
    } catch (e) {}

    return null;
  };

  const loadOnlyOfficeScript = () => {
    if (scriptLoaded.current) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const dsBase =
        process.env.REACT_APP_ONLYOFFICE_URL ||
        process.env.REACT_APP_DS_URL ||
        'http://ds.onefincore.com';

      const isLocal = dsBase.includes('localhost') || dsBase.includes('127.0.0.1');
      const scriptPath = isLocal
        ? '/web-apps/apps/api/documents/api.js'
        : '/onlyoffice/web-apps/apps/api/documents/api.js';

      const scriptUrl = `${dsBase}${scriptPath}`;

      const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
      if (existingScript && window.DocsAPI?.DocEditor) {
        scriptLoaded.current = true;
        resolve();
        return;
      }

      const script = existingScript || document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        const check = () => {
          if (window.DocsAPI?.DocEditor) {
            console.log(' OnlyOffice SDK loaded successfully');
            scriptLoaded.current = true;
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      };

      script.onerror = (err) => {
        console.error('❌ Failed to load OnlyOffice script:', scriptUrl, err);
        toast.error('OnlyOffice SDK failed to load.');
        reject(err);
      };

      if (!existingScript) {
        document.head.appendChild(script);
      }
    });
  };

  const getSignatureImage = () => {
    if (signatureMode === 'draw') {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        return signaturePadRef.current.toDataURL('image/png');
      }
    } else if (signatureMode === 'type') {
      if (!typedName.trim()) return null;

      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 120;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 45px "Lucida Handwriting", cursive, "Brush Script MT", sans-serif';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName.trim(), canvas.width / 2, canvas.height / 2);

      return canvas.toDataURL('image/png');
    } else if (signatureMode === 'upload' && uploadedImage) {
      return uploadedImage;
    }

    return null;
  };

  const resetSignatureState = () => {
    setShowSignatureModal(false);
    setTypedName('');
    setUploadedImage(null);
    if (signaturePadRef.current?.clear) {
      signaturePadRef.current.clear();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setUploadedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const reportSignatureResult = (result) => {
    setSignaturePlacementPending(result === 'needs-placement');
    resetSignatureState();
    if (result === 'needs-placement') {
      toast.info('Signature prepared. In the document editor choose Insert → Image → Image from Storage to place it. For PDF, use Edit PDF mode.');
    } else if (result === 'sent') {
      toast.info('Signature sent to the current document. Position it and save your changes in the editor.');
    }
  };

  const handleApplySignature = async () => {
    const image = getSignatureImage();
    if (!image) { toast.error('Please provide a signature first'); return; }
    if (isSigning) return;
    setIsSigning(true);
    try { reportSignatureResult(await signatureFlow.current.submit(image)); }
    catch (error) { toast.error(error.response?.data?.message || error.message || 'Could not insert signature'); }
    finally { setIsSigning(false); }
  };

  const startEditorWithPayload = async (payload, force = false) => {
    const newDocId = payload?.docId;
    if (!newDocId) {
      clearDocument();
      return;
    }

    if (!force && String(newDocId) === String(currentDocId)) return;

    setCurrentDocId(newDocId);
    setDocName(payload.docName || `Document ${newDocId}`);
    setSessionId(String(payload.sessionId));
    const loadVersion = editorLoad.current + 1;

    try {
      setIsLoading(true);
      destroyEditor();

      const { resolvedRole, displayName, clientId: cid } = resolveIdentity();
      setRole(resolvedRole);
      setClientId(cid);
      setActionLabel(resolvedRole === 'client_admin' ? 'Close' : 'End');

      const editor = await getCurrentEditorForSession(payload.sessionId);
      setCurrentEditor(editor);

      const canEdit = canEditSessionDocument(editor, cid, resolvedRole);
      requestedEdit.current = canEdit;
      setCurrentCanEdit(canEdit);

      const res = await getDocumentEditorConfig(newDocId, {
        userId: cid,
        displayName,
        role: resolvedRole,
        canEdit,
      });

      if (loadVersion !== editorLoad.current) return;
      const baseConfig = res?.data;
      if (!baseConfig?.document?.url) {
        throw new Error('Invalid OnlyOffice config');
      }

      const fileType = (baseConfig.document.fileType || '').toLowerCase();

setIsSignable(['pdf', 'docx', 'doc', 'odt'].includes(fileType));

      await loadOnlyOfficeScript();
      if (loadVersion !== editorLoad.current) return;
      const editable = canEdit && baseConfig.editorConfig?.mode === 'edit' && baseConfig.document?.permissions?.edit !== false;
      setCurrentCanEdit(editable);

      const mergedConfig = {
        ...baseConfig,
        type: 'desktop',
        width: '100%',
        height: '100%',
        editorConfig: {
          ...(baseConfig.editorConfig || {}),
          mode: editable ? 'edit' : 'view',
        },
                        events: {
  onAppReady: () => {
    console.log(" OnlyOffice Ready");
  },

  onDocumentReady: () => {
    if (loadVersion !== editorLoad.current) return;
    console.log(" Document loaded successfully");
    setIsLoading(false);

    if (loadVersion === editorLoad.current) signatureFlow.current.ready(newDocId);
  },

  onRequestInsertImage: async (event) => {
    if (loadVersion !== editorLoad.current) return;
    try {
      const result = await signatureFlow.current.request(newDocId, event?.data?.c || 'add');
      if (result === 'needs-signature') setShowSignatureModal(true);
      else reportSignatureResult(result);
    } catch (error) { toast.error(error.response?.data?.message || error.message || 'Could not insert signature'); }
  },

  onError: (e) => {
    console.error("❌ OnlyOffice Error:", e);
    setIsLoading(false);
  },

  onRequestEditRights: () => {
    console.log("✏️ Edit rights requested");
  },

},
      }

      editorInstance.current = mountOnlyOfficeEditor(
        editorHost.current, window.DocsAPI.DocEditor, mergedConfig
      );
      signatureFlow.current.activate(newDocId, editorInstance.current, editable);
    } catch (err) {
      if (loadVersion !== editorLoad.current) return;
      console.error('Failed to start editor:', err);
      toast.error('Could not open document');
      clearDocument();
    }
  };


  const leaveAfterSessionEnd = () => {
    if (!sessionId) {
      navigate('/signin', { replace: true });
      return;
    }

    if (role === 'client_admin') {
      navigate('/session', { replace: true });
      return;
    }

    const cid = clientId;
    if (sessionId && cid) {
      localStorage.setItem(`left_session_${sessionId}_${cid}`, 'true');
      try {
        const bc = bcRef.current || new BroadcastChannel('presentation_channel');
        bc.postMessage({ type: 'participant_left', sessionId, userId: cid });
        if (!bcRef.current) bc.close();
      } catch (e) {}
    }

    localStorage.removeItem('join_request');
    localStorage.removeItem('currentSession');
    navigate('/signin', { replace: true }); 
  };

  const handleSessionFinishedAction = () => {
    if (autoRedirectTimeoutRef.current) {
      clearTimeout(autoRedirectTimeoutRef.current);
      autoRedirectTimeoutRef.current = null;
    }
    leaveAfterSessionEnd();
  };

  useEffect(() => {
    if (sessionFinished) {
      destroyEditor();
      setIsLoading(false);
      if (pollRef.current) clearInterval(pollRef.current);

      if (role && role !== 'client_admin') {
        autoRedirectTimeoutRef.current = setTimeout(() => {
          leaveAfterSessionEnd();
        }, 5000);
      }
    }

    return () => {
      if (autoRedirectTimeoutRef.current) {
        clearTimeout(autoRedirectTimeoutRef.current);
      }
    };
  }, [sessionFinished, role]);

  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('presentation_channel');
      bcRef.current.onmessage = (ev) => {
        const msg = ev.data;
        if (!msg) return;

        if (msg.docId && !msg.type) {
          if (!sessionId || String(msg.sessionId) === String(sessionId)) {
            startEditorWithPayload(msg);
          }
          return;
        }

        if (!sessionId || String(msg.sessionId) !== String(sessionId)) return;

        if (msg.type === 'editor_changed') {
          const editor = msg.current_editor || null;
          setCurrentEditor(editor);
          try {
            localStorage.setItem(
              `presentation_editor_${msg.sessionId}`,
              JSON.stringify(editor)
            );
          } catch (e) {}
        }

        if (msg.type === 'document_closed') {
          clearDocument();
        }

        if (msg.type === 'session_finished') {
          setSessionFinished(true);
        }
      };
    } catch (e) {}

    const onStorage = (e) => {
      if (!e.key) return;

      if (e.key.startsWith('presentation_') && !e.key.includes('editor')) {
        const keySessionId = e.key.split('presentation_')[1];
        if (String(keySessionId) !== String(sessionId)) return;

        if (e.newValue === null) {
          clearDocument();
          return;
        }

        try {
          const payload = JSON.parse(e.newValue || '{}');
          if (payload.docId) {
            startEditorWithPayload(payload);
          } else {
            clearDocument();
          }
        } catch {
          clearDocument();
        }
      }

      if (sessionId && e.key === `session_finished_${sessionId}` && e.newValue) {
        setSessionFinished(true);
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (bcRef.current) bcRef.current.close();
    };
  }, [sessionId, currentDocId]);

  useEffect(() => {
    let payload = state;

    if (!payload) {
      const key = Object.keys(localStorage).find(
        (k) => k.startsWith('presentation_') && !k.includes('editor')
      );
      if (key) {
        try {
          payload = JSON.parse(localStorage.getItem(key) || '{}');
        } catch {}
      }
    }

    if (!payload?.sessionId) {
      toast.error(displayText('No session found'));
      navigate('/session', { replace: true });
      return;
    }

    const { resolvedRole } = resolveIdentity();
    setRole(resolvedRole);
    setActionLabel(resolvedRole === 'client_admin' ? 'Close' : 'End');
    setSessionId(String(payload.sessionId));

    try {
      const finishedFlag = localStorage.getItem(`session_finished_${payload.sessionId}`);
      if (finishedFlag) {
        setSessionFinished(true);
      }
    } catch (e) {}

    if (payload.docId && !sessionFinished) {
      startEditorWithPayload(payload);
      return;
    }

    setDocName('Waiting for document from presenter...');

    pollRef.current = setInterval(async () => {
      if (sessionFinished) return;

      try {
        const storedKey = `presentation_${payload.sessionId}`;
        const stored =
          localStorage.getItem(storedKey) ||
          sessionStorage.getItem(storedKey);

        if (stored === null && currentDocId) {
          clearDocument();
          return;
        }

        if (stored) {
          const p = JSON.parse(stored);
          if (p.docId && String(p.docId) !== String(currentDocId)) {
            clearInterval(pollRef.current);
            startEditorWithPayload(p);
            return;
          }
          if (!p.docId && currentDocId) {
            clearDocument();
          }
        }

        const res = await getSessionStatus(payload.sessionId);
        const data = res?.data?.data || res?.data || {};

        if (typeof data.current_editor !== 'undefined') {
          localStorage.setItem(
            `presentation_editor_${payload.sessionId}`,
            JSON.stringify(data.current_editor)
          );
          setCurrentEditor(data.current_editor);
        }

        if (data.status && data.status.toLowerCase() === 'finished') {
          setSessionFinished(true);
          return;
        }

        const docId =
          data.presentation_doc_id ||
          data.current_document_id ||
          data.document_id ||
          data.document?.id;

        if (docId && String(docId) !== String(currentDocId)) {
          clearInterval(pollRef.current);
          startEditorWithPayload({
            docId,
            docName:
              data.document?.title ||
              data.presentation_doc_name ||
              `Document ${docId}`,
            sessionId: payload.sessionId,
          });
        } else if (!docId && currentDocId) {
          clearDocument();
        }
      } catch (err) {
        // ignore polling errors
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [state, navigate]);

  useEffect(() => {
    return () => {
      destroyEditor();
      if (pollRef.current) clearInterval(pollRef.current);
      if (bcRef.current) bcRef.current.close();
      if (autoRedirectTimeoutRef.current) clearTimeout(autoRedirectTimeoutRef.current);
      if (signaturePadRef.current?.clear) signaturePadRef.current.clear();
    };
  }, []);

  const handleHeaderAction = () => {
    if (role === 'client_admin') {
      const key = `presentation_${sessionId}`;
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);

      try {
        const bc = new BroadcastChannel('presentation_channel');
        bc.postMessage({ type: 'document_closed', sessionId });
        bc.close();
      } catch (e) {}

      navigate(-1);
    } else {
      const cid = clientId;
      if (sessionId && cid) {
        localStorage.setItem(`left_session_${sessionId}_${cid}`, 'true');
        const bc = bcRef.current || new BroadcastChannel('presentation_channel');
        bc.postMessage({ type: 'participant_left', sessionId, userId: cid });
        if (!bcRef.current) bc.close();
      }

      localStorage.removeItem('join_request');
      localStorage.removeItem('currentSession');
      navigate('/signin', { replace: true });
    }
  };

  useEffect(() => {
    if (!currentDocId || isLoading || sessionFinished || requestedEdit.current === null) return;
    const allowed = canEditSessionDocument(currentEditor, clientId, role);
    if (allowed !== requestedEdit.current) {
      startEditorWithPayload({ docId: currentDocId, docName, sessionId }, true);
    }
  }, [currentEditor, currentDocId, clientId, role, isLoading, sessionFinished]);

  const signAction = signatureAction({ documentId: currentDocId, loading: isLoading,
    signable: isSignable, canEdit: currentCanEdit, signing: isSigning, finished: sessionFinished });
  const canUseSignButton = signAction.enabled;

  return (
    <div
      style={{
        height: '100vh',
        width: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        fontFamily,
        fontSize: `${bodyFontSize}px`,
      }}
    >
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          background: '#222',
          color: '#fff',
          zIndex: 9999,
          fontFamily,
        }}
      >
        <div
          style={{
            fontSize: titleFontSize ? `${titleFontSize}px` : 15,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          {docName}
          {currentCanEdit && (
            <span style={{ marginLeft: 10, fontSize: 12, opacity: 0.8 }}>
              Editing
            </span>
          )}
          {currentEditor && !currentCanEdit && (
            <span style={{ marginLeft: 10, fontSize: 12, opacity: 0.8 }}>
              View Only
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {(
  <button
    onClick={() => {
      if (!canUseSignButton) return;
      setShowSignatureModal(true);
    }}
    disabled={!canUseSignButton}
    title={signAction.reason}
    aria-describedby={!canUseSignButton && currentDocId && !isLoading ? "signature-access-help" : undefined}
    style={{
      background: canUseSignButton ? '#28a745' : '#9aa0a6',
      color: '#fff',
      border: 'none',
      padding: '8px 16px',
      borderRadius: 6,
      fontWeight: 600,
      cursor: canUseSignButton ? 'pointer' : 'not-allowed',
      opacity: canUseSignButton ? 1 : 0.7,
      fontFamily,
      fontSize: `${bodyFontSize}px`,
    }}
  >
    Sign
  </button>
)}

          <button
            onClick={handleHeaderAction}
            style={{
              background: role === 'client_admin' ? '#6c757d' : '#e74c3c',
              color: '#fff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>

      {!canUseSignButton && currentDocId && !isLoading && <div id="signature-access-help" role="status" style={{ padding: '8px 16px', fontFamily, fontSize: bodyFontSize, background: theme?.colors?.bgSecondary }}>
        {signAction.reason}
      </div>}
      {signaturePlacementPending && <div role="status" style={{ padding: '10px 16px', background: theme?.colors?.bgSecondary, fontFamily, fontSize: bodyFontSize }}>
        Signature ready for {docName}. In the editor choose <strong>Insert → Image → Image from Storage</strong> to place it.
        For PDF, switch to <strong>Edit PDF</strong> first. Save after positioning the signature.
        <button onClick={() => { signatureFlow.current.cancel(); setSignaturePlacementPending(false); }} style={{ marginLeft: 12 }}>Cancel signature</button>
      </div>}
      <div
        ref={editorHost}
        data-testid="document-editor-host"
        style={{ height: 'calc(100vh - 48px)', width: '100%' }}
      />

      {sessionFinished && (
        <div
          style={{
            position: 'fixed',
            top: 48,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            fontFamily,
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: 8,
              padding: '20px 24px',
              maxWidth: 360,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            <h3
              style={{
                marginBottom: 12,
                fontSize: `${titleFontSize}px`,
              }}
            >{displayText(" Session completed ")}</h3>
            <p
              style={{
                fontSize: `${bodyFontSize}px`,
                marginBottom: 20,
                color: '#444',
              }}
            >{displayText(" This session has been marked as finished. ")}{role && role !== 'client_admin'
                ? ' You will be redirected to sign in in a few seconds.'
                : displayText(' You can return to the sessions list.')}
            </p>
            <button
              onClick={handleSessionFinishedAction}
              style={{
                background: role === 'client_admin' ? '#6c757d' : '#e74c3c',
                color: '#ffffff',
                border: 'none',
                padding: '8px 18px',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: `${bodyFontSize}px`,
                fontFamily,
              }}
            >
              {role === 'client_admin' ? displayText('Back to Sessions') : displayText('Leave Session')}
            </button>
          </div>
        </div>
      )}

      {showSignatureModal && (
        <div
          style={{
            position: 'fixed',
            top: 48,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100001,
            fontFamily,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: '24px',
              width: 520,
              maxWidth: '92%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: `${titleFontSize}px`, textAlign: 'center' }}>
              Sign Document
            </h3>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
              {['draw', 'type', 'upload'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setSignatureMode(mode)}
                  style={{
                    padding: '8px 24px',
                    background: signatureMode === mode ? '#007bff' : '#f8f9fa',
                    color: signatureMode === mode ? '#fff' : '#333',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {mode === 'draw' ? 'Draw' : mode === 'type' ? 'Type Name' : 'Upload Image'}
                </button>
              ))}
            </div>

            <div
              style={{
                border: '1px solid #ccc',
                borderRadius: 6,
                background: '#fff',
                minHeight: 140,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {signatureMode === 'draw' && (
                <SignatureCanvas
                  ref={signaturePadRef}
                  penColor="black"
                  canvasProps={{
                    width: 480,
                    height: 140,
                    className: 'sigCanvas',
                  }}
                />
              )}

              {signatureMode === 'type' && (
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <input
                    type="text"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your name"
                    style={{
                      width: '100%',
                      fontSize: 24,
                      fontFamily: 'cursive',
                      border: 'none',
                      outline: 'none',
                      textAlign: 'center',
                      background: 'transparent',
                    }}
                  />
                  <div
                    style={{
                      marginTop: 20,
                      fontSize: 25,
                      fontFamily: '"Lucida Handwriting", cursive, "Brush Script MT", sans-serif',
                      color: '#222',
                      minHeight: 60,
                      lineHeight: '60px',
                    }}
                  >
                    {typedName || 'Your signature preview'}
                  </div>
                </div>
              )}

              {signatureMode === 'upload' && (
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <input type="file" accept="image/*" onChange={handleFileUpload} />
                  {uploadedImage && (
                    <img
                      src={uploadedImage}
                      alt="preview"
                      style={{ maxHeight: 140, maxWidth: '100%', marginTop: 20, objectFit: 'contain' }}
                    />
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                onClick={() => { signatureFlow.current.cancel(); resetSignatureState(); }}
                style={{
                  padding: '10px 24px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleApplySignature}
                disabled={isSigning}
                style={{
                  padding: '10px 24px',
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {isSigning ? 'Adding signature…' : 'Use Signature'}
              </button>
            </div>

            <p style={{ textAlign: 'center', fontSize: 12, color: '#666', marginTop: 16 }}>
              Place the signature using Insert → Image → Image from Storage in the editor. For PDF, switch to Edit PDF first. Save the document after placement.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Presentation;